import { useNavigate } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useEnvironment } from '@/features/environments/contexts/EnvironmentContext'
import { Button } from '@/shared/components/ui/button'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import type { Contract } from '../../services/contractService'
import { contractService } from '../../services/contractService'
import type { Customer } from '../../services/customerService'
import { ContractListTable } from '../contracts/ContractListTable'

interface ContractTabProps {
  customer: Customer
}

export function ContractTab({ customer }: ContractTabProps) {
  const navigate = useNavigate()
  const { currentEnvironment } = useEnvironment()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  useEffect(() => {
    const loadContracts = async () => {
      setLoading(true)
      try {
        const response = await contractService.getContracts({
          customer_id: customer.id,
        })
        setContracts(response.data)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      } finally {
        setLoading(false)
      }
    }

    loadContracts()
  }, [customer.id, currentEnvironment?.id])

  const handleContractAction = async (
    _contractId: string,
    _action: 'activate' | 'pause' | 'cancel' | 'resume'
  ) => {
    try {
      // Refresh contracts list after action
      const response = await contractService.getContracts({
        customer_id: customer.id,
      })
      setContracts(response.data)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-center'>
        <h2 className='text-lg font-semibold'>Contracts</h2>
        <Button
          onClick={() =>
            navigate({
              to: `/customers/${customer.id}/contracts/create`,
            })
          }
          className='gap-2'
        >
          <Plus className='w-4 h-4' />
          Create Contract
        </Button>
      </div>

      {/* Contract List Table */}
      {!loading && contracts.length === 0 ? (
        <div className='text-center py-12 text-muted-foreground'>
          <h3 className='text-lg font-medium mb-2'>No contracts found</h3>
          <p className='text-sm max-w-sm mx-auto mb-4'>
            This customer doesn't have any contracts yet.
          </p>
          <Button
            onClick={() =>
              navigate({
                to: `/customers/${customer.id}/contracts/create`,
              })
            }
          >
            Create First Contract
          </Button>
        </div>
      ) : (
        <ContractListTable
          contracts={contracts as never}
          loading={loading}
          onContractAction={handleContractAction}
        />
      )}
    </div>
  )
}
